import React, { useMemo, useState } from "react";
import { Box, CircularProgress, Chip, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Sidebar from "components/sidebar/Sidebar";
import { useSelector } from "react-redux";
import { selectSidebarCollapsed } from "store/slices/app/appSelector";
import { useGetUserCompaniesQuery } from "store/apis/companyApi";
import { useGetUserDbCampaignsQuery } from "store/apis/campaignApi";
import { useGetAllCampaignsMetricsQuery } from "store/apis/internalCampaignsMetricsApi";
import EmptyState from "components/EmptyState";
import CampaignCard from "components/card/CampaignCard";
import StatisticsSection from "../components/metrics/StatisticsSection";
import MetricsTimelineChart from "../components/card/MetricsTimelineChart";
import PlatformDistributionChart from "../components/card/PlatformDistributionChart";
import GoalDistributionChart from "../components/card/GoalDistributionChart";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";

const Home: React.FC = () => {
  const collapsed = useSelector(selectSidebarCollapsed);
  const { t } = useTranslation();

  // Fetch user companies and campaigns
  const {
    data: companiesData,
    isLoading: isLoadingCompanies,
    error: companiesError,
  } = useGetUserCompaniesQuery();

  const {
    data: campaignsData,
    isLoading: isLoadingCampaigns,
    error: campaignsError,
  } = useGetUserDbCampaignsQuery();

  // Date filter state
  type DateFilter = "all" | "month" | "week";
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");

  // Calculate date ranges based on filter
  const dateParams = useMemo(() => {
    if (dateFilter === "all") {
      return undefined; // Don't send dates for "all time"
    } else if (dateFilter === "month") {
      return {
        startDate: dayjs().subtract(1, "month").format("YYYY-MM-DD"),
        endDate: dayjs().format("YYYY-MM-DD"),
      };
    } else if (dateFilter === "week") {
      return {
        startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
        endDate: dayjs().format("YYYY-MM-DD"),
      };
    }
    return undefined;
  }, [dateFilter]);

  // Fetch metrics data for the dashboard with date filters
  const {
    data: metricsData,
    isLoading: isLoadingMetrics,
    // We're not using the error yet, but might need it in the future
  } = useGetAllCampaignsMetricsQuery(dateParams);

  // Map API campaign data to the format expected by CampaignCard
  const mappedCampaigns = useMemo(() => {
    if (!campaignsData?.data) return [];

    return campaignsData.data.slice(0, 5).map((campaign) => ({
      id: campaign.id,
      name: campaign.displayName,
      date: dayjs(campaign.startDate).format("DD MMM"),
      platform: campaign.platform, // Using platform value directly from API
    }));
  }, [campaignsData]);

  // Check if user has companies and campaigns
  const hasCompanies = companiesData?.data && companiesData.data.length > 0;
  const hasCampaigns = campaignsData?.data && campaignsData.data.length > 0;

  // Combined loading state
  const isLoading =
    isLoadingCompanies || isLoadingCampaigns || isLoadingMetrics;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />

      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
          width: { xs: "calc(100% - 80px)", sm: "auto" },
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
          minHeight: "100vh",
        }}
      >
        <PageBreadcrumbs />

        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : companiesError || !hasCompanies ? (
          <EmptyState
            title={t("home.emptyStates.noCompanies.title")}
            message={t("home.emptyStates.noCompanies.message")}
            buttonText={t("home.emptyStates.noCompanies.buttonText")}
            buttonLink="/companies"
          />
        ) : campaignsError || !hasCampaigns ? (
          <EmptyState
            title={t("home.emptyStates.noCampaigns.title")}
            message={t("home.emptyStates.noCampaigns.message")}
            buttonText={t("home.emptyStates.noCampaigns.buttonText")}
            buttonLink="/campaigns/new"
          />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {/* Date Filter Chips */}
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", gap: 0, rowGap: 1 }}
              >
                <Chip
                  label={t("common.allTime")}
                  onClick={() => setDateFilter("all")}
                  color={dateFilter === "all" ? "primary" : "default"}
                  variant={dateFilter === "all" ? "filled" : "outlined"}
                />
                <Chip
                  label={t("common.last30days")}
                  onClick={() => setDateFilter("month")}
                  color={dateFilter === "month" ? "primary" : "default"}
                  variant={dateFilter === "month" ? "filled" : "outlined"}
                />
                <Chip
                  label={t("common.lastSevenDays")}
                  onClick={() => setDateFilter("week")}
                  color={dateFilter === "week" ? "primary" : "default"}
                  variant={dateFilter === "week" ? "filled" : "outlined"}
                />
              </Stack>
            </Box>
            {/* Statistics (2x2) - left, Campaign Card - right */}
            <Box
              sx={{
                display: "flex",
                gap: { xs: "16px", sm: "24px" },
                mt: { xs: "16px", sm: "24px" },
                alignItems: "stretch",
                flexWrap: { xs: "wrap", lg: "nowrap" },
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {/* Statistics Cards Section - 2x2 grid */}
              <Box
                sx={{
                  flex: { xs: "1 1 100%", lg: "0 0 calc(50% - 12px)" },
                  minWidth: { xs: "100%", lg: "auto" },
                  width: "100%",
                }}
              >
                {metricsData && (
                  <StatisticsSection
                    summary={metricsData.summary}
                    columns={2}
                    fillHeight
                  />
                )}
              </Box>

              {/* Campaign Card */}
              <Box
                sx={{
                  flex: { xs: "1 1 100%", lg: "0 0 calc(50% - 12px)" },
                  minWidth: { xs: "100%", lg: "auto" },
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <CampaignCard
                  title={t("home.campaigns")}
                  subtitle={t("home.activeCampaigns", {
                    count: campaignsData?.data?.length || 0,
                  })}
                  campaigns={mappedCampaigns}
                />
              </Box>
            </Box>

            {/* Metrics Timeline Chart - full width, below */}
            {metricsData && (
              <Box
                sx={{
                  width: "100%",
                  boxSizing: "border-box",
                  mt: { xs: "16px", sm: "24px" },
                }}
              >
                <MetricsTimelineChart
                  title={t("home.metricsTimeline")}
                  subtitle={t("home.metricsTimelineSubtitle")}
                  data={metricsData.dailyData.map((day) => ({
                    date: day.date,
                    impressions: day.impressions || 0,
                    clicks: day.clicks || 0,
                    cost: day.cost || 0,
                    // Calculate or provide default values for required metrics
                    ctr:
                      day.clicks && day.impressions
                        ? day.clicks / day.impressions
                        : 0,
                    cpc: day.clicks && day.cost ? day.cost / day.clicks : 0,
                    cpm: day.impressions
                      ? (day.cost / day.impressions) * 1000
                      : 0,
                    completeViews: day.completeViews || 0,
                  }))}
                  isLoading={false}
                  hiddenMetrics={["ctr"]}
                />
              </Box>
            )}

            {/* Distribution Charts Section - 50/50 split */}
            {metricsData &&
              metricsData.goalDistribution &&
              metricsData.platformDistribution && (
                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: "16px", sm: "24px" },
                    mt: { xs: "16px", sm: "24px" },
                    width: "100%",
                    boxSizing: "border-box",
                    flexWrap: { xs: "wrap", md: "nowrap" },
                    overflowX: "auto", // Allow horizontal scrolling if needed
                  }}
                >
                  {/* Platform Distribution Chart - 50% width */}
                  <Box
                    sx={{
                      flex: "0 0 calc(50% - 12px)",
                      minWidth: { xs: "100%", md: "calc(50% - 12px)" },
                    }}
                  >
                    <PlatformDistributionChart
                      title={t("home.platformDistribution")}
                      subtitle={t("home.platformDistributionSubtitle")}
                      data={metricsData.platformDistribution}
                      isLoading={false}
                    />
                  </Box>

                  {/* Goal Distribution Chart - 50% width */}
                  <Box
                    sx={{
                      flex: "0 0 calc(50% - 12px)",
                      minWidth: { xs: "100%", md: "calc(50% - 12px)" },
                    }}
                  >
                    <GoalDistributionChart
                      title={t("home.goalDistribution")}
                      subtitle={t("home.goalDistributionSubtitle")}
                      data={metricsData.goalDistribution}
                      isLoading={false}
                    />
                  </Box>
                </Box>
              )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Home;
